import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CA_PROVINCES, COUNTRIES, DEFAULT_COUNTRY, countryHasProvinces } from "@shared/constants/provinces";
import { PasswordInput } from "@/components/PasswordInput";
import { AuthBrandHeader } from "@/components/AuthBrandHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";
import { checkBreachedPassword } from "@/lib/hibpCheck";
import { FONT, PRODUCT_NAME, SAGE } from "@shared/brand";
import { AlertTriangle, ArrowLeft, Check, X } from "lucide-react";

const signupSchema = z.object({
  // SIGNUP-NAME-PARTS-1 (s203): captured as parts. The server composes the display name from
  // these via deriveFullName and stores the parts in About You — so the planner never retypes
  // a name the app already has.
  firstName: z.string().min(1, "First name is required"),
  middleName: z.string().optional(),
  familyName: z.string().min(1, "Family name is required"),
  email: z.string().email("Invalid email address"),
  phoneNumber: z.string().regex(/^\+?[1-9]\d{9,14}$/, "Please enter a valid phone number with at least 10 digits (e.g., +1234567890)"),
  password: z.string()
    .min(10, "Password must be at least 10 characters")
    .refine(
      p => /[A-Z]/.test(p) && /[a-z]/.test(p) && /[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p),
      "Password must include uppercase, lowercase, number, and special character"
    ),
  confirmPassword: z.string(),
  advisorCode: z.string().optional(),
  country: z.string().min(1, "Please select a country"),
  province: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type SignupFormData = z.infer<typeof signupSchema>;

export default function Signup() {
  const { signup } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const [isLoading, setIsLoading] = useState(false);
  const [advisorName, setAdvisorName] = useState<string | null>(null);
  const [breachCount, setBreachCount] = useState<number | null>(null);

  // Parse URL params
  const params = new URLSearchParams(search);
  const advisorCodeParam = params.get("advisorCode") ?? "";
  const emailParam = params.get("email") ?? "";

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: "",
      middleName: "",
      familyName: "",
      email: emailParam,
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      advisorCode: advisorCodeParam,
      country: DEFAULT_COUNTRY,
      province: "",
    },
  });

  const watchedPassword = form.watch("password");
  const watchedConfirm = form.watch("confirmPassword");
  const watchedCountry = form.watch("country");

  // Validate advisor code on mount if pre-filled
  useEffect(() => {
    if (!advisorCodeParam) return;
    apiRequest("GET", `/api/auth/validate-advisor-code?code=${advisorCodeParam}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) setAdvisorName(data.advisorName);
      })
      .catch(() => {});
  }, [advisorCodeParam]);

  // D-170.6: converted from a raw apiRequest to the house pattern. Class A — this fires a real
  // account-creation attempt and, for a non-bypass address, a verification email. `users.email`
  // is UNIQUE, so a duplicate account is impossible; what a double-submit actually produces is a
  // second attempt that loses the race and shows the submitter "Email already registered" for
  // their own click. The lock is what stops that.
  //
  // NOTE (finding, s170): this is the SECOND signup implementation — `AuthContext.signup` is the
  // other, and it posts to the same route. Consolidation is out of scope here.
  const signupFlight = useSingleFlight();
  const signupMutation = useMutation({
    mutationFn: async (data: SignupFormData) => {
      const res = await apiRequest("POST", "/api/auth/signup", {
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        middleName: data.middleName || undefined,
        familyName: data.familyName,
        phoneNumber: data.phoneNumber,
        advisorCode: data.advisorCode || undefined,
        country: data.country,
        // Province only applies to countries with a region list (CA today).
        province: countryHasProvinces(data.country) ? (data.province || undefined) : undefined,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Signup failed");
      }
      return await res.json();
    },
  });

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      const result = await signupFlight.run(() => signupMutation.mutateAsync(data));
      // A skipped duplicate does nothing — no toast, no navigation. The in-flight original
      // still owns the outcome.
      if (!result) return;
      // Hard email-verification gate (s90): real signups get no session — send them to
      // the interstitial to verify before they can log in. Test-bypass emails auto-verify
      // server-side (emailVerificationRequired:false) and proceed straight in.
      if (result.emailVerificationRequired) {
        try { localStorage.setItem("pendingVerificationEmail", data.email); } catch {}
        toast({ title: "Account created!", description: "Check your email to verify your account." });
        setLocation(`/verify-email?email=${encodeURIComponent(data.email)}`);
        return;
      }
      queryClient.clear();
      toast({ title: "Account created!", description: "Your account has been successfully created." });
      setLocation("/dashboard");
    } catch (error: any) {
      toast({ title: "Signup failed", description: error.message || "Failed to create account", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="flex flex-col items-center w-full max-w-md">
        <div className="mb-3">
          <Link href="/">
            <span className="text-sm text-muted-foreground hover:text-primary cursor-pointer inline-flex items-center gap-1" data-testid="link-back-home"><ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />Back to home</span>
          </Link>
        </div>
        <AuthBrandHeader />
        <Card className="w-full">
        <CardHeader>
          <CardTitle data-testid="text-signup-title" style={{ fontFamily: FONT.serif }}>Create Account</CardTitle>
          <CardDescription data-testid="text-signup-description">
            Start organizing your estate plan — protect the people you love.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {advisorName && (
            <div
              className="mb-5 rounded-md px-4 py-3 text-sm"
              style={{ backgroundColor: SAGE[600], color: "#fff" }}
            >
              <span className="font-semibold">{advisorName}</span> has invited you to {PRODUCT_NAME}.
              {" "}Their referral code is applied to your account.
            </div>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* SIGNUP-NAME-PARTS-1 (s203): three boxes, not one. The parts are what About You
                  stores, so the planner never retypes a name the app already captured. Middle name
                  is genuinely optional and says so. */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField control={form.control} name="firstName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="John" data-testid="input-firstname" disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="middleName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Middle Name <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="" data-testid="input-middlename" disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="familyName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Family Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Doe" data-testid="input-familyname" disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <FormField control={form.control} name="email" render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="you@example.com" data-testid="input-email" disabled={isLoading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="phoneNumber" render={({ field }) => (
                <FormItem>
                  <FormLabel>Mobile Number</FormLabel>
                  <FormControl>
                    <Input {...field} type="tel" placeholder="+1234567890" data-testid="input-phone" disabled={isLoading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="country" render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={(v) => {
                      field.onChange(v);
                      // Clear any picked province when switching to a country without a region list.
                      if (!countryHasProvinces(v)) form.setValue("province", "");
                    }}
                    disabled={isLoading}
                  >
                    <FormControl>
                      <SelectTrigger data-testid="select-country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code} data-testid={`country-${c.code}`}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />

              {countryHasProvinces(watchedCountry) && (
                <FormField control={form.control} name="province" render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Province / Territory <span className="text-muted-foreground">(optional)</span>
                    </FormLabel>
                    <Select value={field.value || undefined} onValueChange={field.onChange} disabled={isLoading}>
                      <FormControl>
                        <SelectTrigger data-testid="select-province">
                          <SelectValue placeholder="Select province" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CA_PROVINCES.map((p) => (
                          <SelectItem key={p.code} value={p.code} data-testid={`province-${p.code}`}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              )}
              <FormField control={form.control} name="password" render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      placeholder="Enter password"
                      data-testid="input-password"
                      disabled={isLoading}
                      onBlur={async (e) => {
                        field.onBlur();
                        const pwd = e.target.value;
                        if (pwd.length >= 10) {
                          const count = await checkBreachedPassword(pwd);
                          setBreachCount(count);
                        } else {
                          setBreachCount(null);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                  <PasswordStrengthMeter password={watchedPassword} />
                  {breachCount !== null && breachCount > 0 && (
                    <p className="text-xs mt-1" style={{ color: "#C53030" }}>
                      <AlertTriangle className="h-3.5 w-3.5 inline-block shrink-0 align-[-2px]" aria-hidden="true" /> This password has appeared in {breachCount.toLocaleString()} data breach{breachCount === 1 ? "" : "es"}. Choose a different password.
                    </p>
                  )}
                </FormItem>
              )} />
              <FormField control={form.control} name="confirmPassword" render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <PasswordInput {...field} placeholder="Confirm password" data-testid="input-confirm-password" disabled={isLoading} />
                  </FormControl>
                  <FormMessage />
                  {watchedConfirm.length > 0 && (
                    <p
                      className="text-xs mt-1"
                      style={{ color: watchedConfirm === watchedPassword ? SAGE[600] : "#C53030" }}
                      data-testid="text-password-match"
                    >
                      {watchedConfirm === watchedPassword
                    ? <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" aria-hidden="true" />Passwords match</span>
                    : <span className="inline-flex items-center gap-1"><X className="h-3.5 w-3.5" aria-hidden="true" />Passwords don't match</span>}
                    </p>
                  )}
                </FormItem>
              )} />

              <FormField control={form.control} name="advisorCode" render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Advisor code <span className="text-muted-foreground">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. SMITH-FP-3847"
                      disabled={isLoading}
                      onChange={e => {
                        field.onChange(e);
                        setAdvisorName(null);
                      }}
                      onBlur={async e => {
                        field.onBlur();
                        const code = e.target.value.trim();
                        if (!code) return;
                        try {
                          const res = await apiRequest("GET", `/api/auth/validate-advisor-code?code=${code}`);
                          const data = await res.json();
                          setAdvisorName(data.valid ? data.advisorName : null);
                        } catch {}
                      }}
                    />
                  </FormControl>
                  {advisorName && (
                    <p className="text-sm text-primary">Code valid — linked to {advisorName}</p>
                  )}
                  <FormMessage />
                </FormItem>
              )} />

              <Button type="submit" className="w-full" data-testid="button-signup" disabled={isLoading || signupFlight.inFlight}>
                {isLoading ? "Creating account..." : "Create account"}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link href="/login">
              <span className="text-primary hover:underline cursor-pointer" data-testid="link-login">Sign in</span>
            </Link>
          </p>
        </CardFooter>
        </Card>
      </div>
    </div>
  );
}
